<template>
  <v-container grid-list-lg fluid>
    <loading v-if="!isLoaded" />

    <v-layout row wrap v-if="isLoaded">
      <v-flex xs12 pb-0 mb-0>
        <h1>{{ task.name }}</h1>
      </v-flex>
      <v-flex xs12 py-0>
        <v-divider />
      </v-flex>
      <v-flex xs12 py-0>
        <span @click="$router.go(-1)" class="goback">{{
          $t("components.tasks.add.gotoBack")
        }}</span>
      </v-flex>
    </v-layout>

    <v-form ref="form" v-model="valid" lazy-validation v-if="isLoaded">
      <v-layout form__row row wrap>
        <v-flex xs3>
          <v-autocomplete
            v-model="task.type"
            :items="tasksTypes"
            :label="$t('components.tasks.add.taskType')"
            item-text="name"
            item-value="value"
            :rules="rules.type"
            :error-messages="errorMessages.type"
            :disabled="!editMode"
            required
          ></v-autocomplete>
        </v-flex>

        <v-flex xs3>
          <v-autocomplete
            v-model="task.status"
            :items="taskStatuses"
            :label="$t('components.tasks.add.taskStatus')"
            item-text="name"
            item-value="value"
            :rules="rules.rule"
            :disabled="!editMode"
            required
          ></v-autocomplete>
        </v-flex>

        <v-flex xs6>
          <v-text-field
            v-model="task.name"
            :label="$t('components.tasks.add.taskName')"
            :rules="rules.rule"
            :disabled="!editMode"
            required
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-layout form__row row wrap>
        <v-flex xs12>
          <v-autocomplete
            v-model="taskUsers"
            :items="people"
            chips
            color="primary"
            :label="$t('components.tasks.add.responsible')"
            item-text="full_name"
            item-value="id"
            multiple
          >
            <template v-slot:selection="data">
              <v-chip
                v-bind="data.attrs"
                :input-value="data.selected"
                close
                @click="data.select"
                @click:close="remove(data.item)"
              >
                <v-avatar left>
                  <v-img
                    :src="
                      data.item.avatar
                        ? data.item.avatar
                        : 'https://cdn.vuetifyjs.com/images/lists/1.jpg'
                    "
                  ></v-img>
                </v-avatar>
                {{ data.item.full_name }}
              </v-chip>
            </template>
            <template v-slot:item="data">
              <template v-if="typeof data.item !== 'object'">
                <v-list-item-content v-text="data.item"></v-list-item-content>
              </template>
              <template v-else>
                <v-list-item-avatar>
                  <img
                    :src="
                      data.item.avatar
                        ? data.item.avatar
                        : 'https://cdn.vuetifyjs.com/images/lists/1.jpg'
                    "
                  />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title
                    v-html="data.item.full_name"
                  ></v-list-item-title>
                  <v-list-item-subtitle>{{
                    data.item.role | formatRole
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </template>
          </v-autocomplete>
        </v-flex>
      </v-layout>

      <v-layout form__row row wrap>
        <v-flex xs4>
          <v-menu
            ref="date_start"
            v-model="dateStart"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="date_start"
                :label="$t('components.tasks.add.dateStart')"
                prepend-icon="event"
                v-on="on"
                v-on:blur="formatDateStart"
                :rules="rules.rule"
                v-mask="'##.##.####'"
                :disabled="!editMode"
                return-masked-value
                required
              ></v-text-field>
            </template>
            <v-date-picker
              ref="picker"
              v-model="task.date_start"
              :max="task.date_end"
              min="2020-01-01"
              @change="saveDateStart"
              llocale="ru-ru"
              first-day-of-week="1"
              no-title
            ></v-date-picker>
          </v-menu>
        </v-flex>

        <v-flex xs2>
          <v-menu
            ref="date_start_time"
            v-model="dateStartTime"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="dateStartTime"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="date_start_time"
                :label="$t('components.tasks.add.time')"
                v-mask="'##:##'"
                prepend-icon="access_time"
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-time-picker
              v-if="dateStartTime"
              v-model="date_start_time"
              format="24hr"
              full-width
              @click:minute="$refs.date_start_time.save(date_start_time)"
            ></v-time-picker>
          </v-menu>
        </v-flex>

        <v-flex xs4>
          <v-menu
            ref="date_end"
            v-model="dateEnd"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="date_end"
                :label="$t('components.tasks.add.dateEnd')"
                prepend-icon="event"
                v-on="on"
                v-on:blur="formatDateEnd"
                :rules="rules.rule"
                v-mask="'##.##.####'"
                :disabled="!editMode"
                return-masked-value
                required
              ></v-text-field>
            </template>
            <v-date-picker
              ref="picker"
              v-model="task.date_end"
              :min="task.date_start"
              max="2022-01-01"
              @change="saveDateEnd"
              llocale="ru-ru"
              first-day-of-week="1"
              no-title
            ></v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs2>
          <v-menu
            ref="date_end_time"
            v-model="dateEndTime"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="dateEndTime"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="date_end_time"
                :label="$t('components.tasks.add.time')"
                v-mask="'##:##'"
                prepend-icon="access_time"
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-time-picker
              v-if="dateEndTime"
              v-model="date_end_time"
              format="24hr"
              full-width
              @click:minute="$refs.date_end_time.save(date_end_time)"
            ></v-time-picker>
          </v-menu>
        </v-flex>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs6>
          <v-select
            v-model="task.event_created"
            :items="tasksReminders"
            :label="$t('components.tasks.add.authorEvent')"
            multiple
            chips
            :hint="$t('components.tasks.add.moreSelectedHint')"
            item-text="name"
            item-value="value"
            persistent-hint
          ></v-select>
        </v-flex>
        <v-flex xs6>
          <v-select
            v-model="task.event_created_author"
            :items="tasksReminders"
            :label="$t('components.tasks.add.reminderEvent')"
            multiple
            chips
            :hint="$t('components.tasks.add.moreSelectedHint')"
            item-text="name"
            item-value="value"
            persistent-hint
          ></v-select>
        </v-flex>
      </v-layout>

      <v-layout form__row row wrap>
        <v-flex xs12>
          <v-textarea
            v-model="task.description"
            :label="$t('components.tasks.add.description')"
            :rules="rules.rule"
            :disabled="!editMode"
            required
          ></v-textarea>
        </v-flex>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12>
          <div class="block">
            <task-chat :task="task" />
          </div>
        </v-flex>
      </v-layout>

      <v-layout form__row form__errors row wrap v-if="errors">
        <v-flex xs12>
          {{ $t("components.tasks.add.checkErrors") }}:
          <strong v-for="(error, idx) in errors" :key="`error_f${idx}`">{{
            error
          }}</strong>
        </v-flex>
      </v-layout>

      <v-layout form__row row wrap v-if="editMode">
        <v-flex xs12>
          <v-btn color="primary" @click="validate" class>{{
            $t("components.tasks.add.submitUpdate")
          }}</v-btn>
        </v-flex>
      </v-layout>
    </v-form>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import { apiCallTrue } from "@/utils/api";
import TaskModel from "@/models/TaskModel";
import rules from "@/utils/data/rules";
import showNotify from "@/services/noty";
import formatRole from "@/mixins/filters/formatRole";
import { mask } from "vue-the-mask";
import TaskChat from "@/components/tasks/chat";
import i18n from "@/services/i18n";

export default {
  name: "Task",
  directives: { mask },
  mixins: [formatRole],
  components: { TaskChat },
  data: () => ({
    valid: false,
    editMode: true,
    task: null,
    isLoaded: false,
    dateStart: false,
    date_start: "",
    dateEnd: false,
    date_start_time: "12:00",
    dateStartTime: false,
    date_end_time: "12:00",
    dateEndTime: false,
    date_end: "",
    errors: null,
    errorMessages: {
      snils: "",
      inn: "",
      type: "",
    },
    newErrorMessages: {
      snils: "",
      inn: "",
      type: "",
    },
    tasksTypes: [
      { name: i18n.t("tasksTypes.10"), value: 10 },
      { name: i18n.t("tasksTypes.20"), value: 20 },
    ],
    tasksReminders: [
      { name: i18n.t("tasksReminders.5"), value: 5 },
      { name: i18n.t("tasksReminders.10"), value: 10 },
      { name: i18n.t("tasksReminders.30"), value: 30 },
      { name: i18n.t("tasksReminders.60"), value: 60 },
      { name: i18n.t("tasksReminders.120"), value: 120 },
      { name: i18n.t("tasksReminders.180"), value: 180 },
    ],
    taskStatuses: [
      { name: i18n.t("taskStatuses.10"), value: 10 },
      { name: i18n.t("taskStatuses.20"), value: 20 },
      { name: i18n.t("taskStatuses.30"), value: 30 },
      { name: i18n.t("taskStatuses.40"), value: 40 },
    ],
    people: null,
    taskUsers: [],
    rules,
  }),
  methods: {
    validate() {
      if (this.$refs.form.validate()) {
        this.saveTask();
      }
    },
    async saveTask() {
      this.errors = null;
      const url = `v1/task/${this.task.id}`;
      const method = "POST";
      const data = Object.assign({}, this.task);
      // data.date_start = `${this.task.date_start.substr(0, 10)} ${
      //     this.dateStartTime
      // }:00`;
      // data.date_end = `${this.task.date_end.substr(0, 10)} ${
      //     this.dateEndTime
      // }:00`;
      data.date_start = `${this.task.date_start.substr(0, 10)} ${
        this.date_start_time
      }`;
      data.date_end = `${this.task.date_end.substr(0, 10)} ${
        this.date_end_time
      }`;
      data.taskUsers = this.taskUsers;

      await apiCallTrue({ url, method, data })
        .then((response) => {
          if (response.data.result) {
            this.notify("task-updated", this.task);
            setTimeout(() => {
              this.$router.push("/tasks");
            }, 1500);
          } else {
            this.notify("response-error");
          }
        })
        .catch((error) => {
          this.errors = {};
          if (error.validation) {
            this.errors = error.validation;
          } else {
            this.errors["message"] = error.message;
          }
        });
    },
    async getTask(id) {
      this.errors = null;
      this.isNewUser = false;
      this.isLoaded = false;

      await apiCallTrue({ url: `v1/task/${id}` })
        .then((response) => {
          this.task = response.data.data;
          this.task.taskUsers.forEach((item) => {
            if (item.type_user !== 1) {
              this.taskUsers.push(item.user.id);
            }
          });
          this.deFormatDateStart();
          this.deFormatDateEnd();
          this.date_start_time = this.getDateTime(this.task.date_start);
          this.date_end_time = this.getDateTime(this.task.date_end);
          this.isLoaded = true;
        })
        .catch((error) => {
          this.errors = {};
          if (error.validation) {
            this.errors = error.validation;
          } else {
            this.errors["message"] = error.message;
          }
        });
    },
    async getPeople() {
      const url = "v1/task/get-responsible";

      await apiCallTrue({ url })
        .then((response) => {
          this.people =
            response && response.data && response.data.data
              ? response.data.data
              : [];
        })
        .catch((error) => console.log("ERROR", error));
    },
    notify(key, data) {
      return showNotify(key, data);
    },
    saveDateStart(date) {
      this.$refs.date_start.save(date);
      this.deFormatDateStart();
    },
    formatDateStart() {
      if (this.date_start) {
        this.task.date_start = this.date_start
          .split(".")
          .reverse()
          .join("-");
      }
    },
    deFormatDateStart() {
      if (this.task.date_start) {
        const clearDate = this.task.date_start.substr(0, 10);
        this.date_start = clearDate
          .split("-")
          .reverse()
          .join(".");
      }
    },
    saveDateEnd(date) {
      this.$refs.date_end.save(date);
      this.deFormatDateEnd();
    },
    formatDateEnd() {
      if (this.date_end) {
        this.task.date_end = this.date_end
          .split(".")
          .reverse()
          .join("-");
      }
    },
    deFormatDateEnd() {
      if (this.task.date_end) {
        const clearDate = this.task.date_end.substr(0, 10);
        this.date_end = clearDate
          .split("-")
          .reverse()
          .join(".");
      }
    },
    remove(item) {
      const index = this.taskUsers.indexOf(item.id);
      if (index >= 0) this.taskUsers.splice(index, 1);
    },
    getDateTime(date) {
      if (!date || !date.length) return "";
      const [timeString] = date.split(" ").reverse();
      return timeString.substr(0, 5);
    },
  },
  computed: {
    ...mapState({
      role: (state) => state.task.profile.role,
    }),
    isValid() {
      if (this.taskname !== "" && this.password !== "") {
        return true;
      }
      return false;
    },
  },
  watch: {
    isCreate(val) {
      if (!val) {
        this.task = Object.assign({}, this.editedTask);
        this.deFormatAssignedAt();
      } else {
        this.task = new TaskModel();
        this.date_created = "";
        this.date_start = "";
        this.date_end = "";
        this.date_completed = "";
        this.event_created = "";
        this.errors = null;
        this.errorMessages = Object.assign({}, this.newErrorMessages);
        this.$refs.form.resetValidation();
      }
    },
    dateStart(val) {
      val && setTimeout(() => (this.$refs.picker.activePicker = "YEAR"));
    },
    dateEnd(val) {
      val && setTimeout(() => (this.$refs.picker.activePicker = "YEAR"));
    },
    isUpdating(val) {
      if (val) {
        setTimeout(() => (this.isUpdating = false), 3000);
      }
    },
  },
  mounted() {
    // this.task = new TaskModel();
    this.getPeople();
    if (this.$route.params.id) {
      this.getTask(this.$route.params.id);
    } else {
      this.isLoaded = true;
    }
  },
};
</script>
